/**
 * pagerPolicy — the single owner of the capsule pager's geometry and the
 * gesture timing windows shared by the web and native pagers and by the
 * transport layer's recognizers. A change to a page step, a snap threshold,
 * a burst window, or the fling-commit rule lands here once.
 *
 * Pure module: no React Native / DOM / network imports, so it is
 * unit-testable in isolation (see __tests__/pagerPolicy.test.ts).
 */

/** Visual separation between capsule pages (scroll step = pageHeight + gap). */
export const PAGE_GAP = 10

/**
 * Snap-back/commit threshold: dragging past this share of a page step
 * commits to the adjacent page; releasing before it snaps back to the
 * current page.
 */
export const THRESHOLD = 0.55

/** Double-tap window — a second tap inside this counts as a double tap. */
export const DOUBLE_TAP_WINDOW_MS = 280

/** Consecutive taps within this window continue an active seek burst. */
export const BURST_WINDOW_MS = 900

/** Press-and-hold duration before the 1.5x speed engages. */
export const LONG_PRESS_MS = 500

/**
 * A physical mouse wheel (deltaMode LINE — Firefox, and most Windows
 * engines) emits one discrete notch per detent. Every notch is its own
 * deliberate capsule step and may chain freely: a fast spin travels one
 * capsule per notch and never skips.
 */
export const WHEEL_LINE_MODE = 1 // WheelEvent.DOM_DELTA_LINE, kept pure

/**
 * Silence between wheel events that ends a scroll gesture. A trackpad
 * gesture (finger motion plus its momentum tail) is a dense stream of
 * frames; mouse notches are separated by human-scale gaps. A gesture may
 * step the feed at most once — this is what stops one two-finger scroll
 * from jumping two capsules.
 */
export const WHEEL_STREAM_IDLE_MS = 120

/**
 * Signed pixel intent a trackpad stream must accumulate before its single
 * step fires (~one mouse notch in pixel engines). Sub-threshold frames
 * (jitter, tiny nudges) never move the feed.
 */
export const WHEEL_STREAM_COMMIT_PX = 40

/**
 * Wheel-gesture state — the single source of truth the web pager feeds
 * every wheel event through. Pure (no DOM), so the regression test drives
 * the exact same contract with synthetic streams.
 */
export type WheelGesture = {
  /** Timestamp of the last wheel event seen (negative = none yet). */
  lastEventAt: number
  /** Signed pixel intent accumulated in the current stream. */
  accum: number
  /**
   * True once this gesture already stepped a capsule — the remaining frames
   * (the momentum tail of the same physical swipe) must never step again.
   */
  absorbed: boolean
  /** Direction of the last significant frame: 1 down, -1 up, 0 none. */
  dir: 1 | -1 | 0
}

export const EMPTY_WHEEL_GESTURE: WheelGesture = {
  lastEventAt: -1,
  accum: 0,
  absorbed: false,
  dir: 0,
}

/**
 * One wheel event → the gesture's next state and whether it steps the feed
 * (+1 next capsule, -1 previous, 0 no step).
 *
 * Two input shapes, two rules:
 *  - LINE mode (a real mouse notch): each event steps one capsule on its
 *    own, chaining freely — a fast spin is one capsule per notch.
 *  - PIXEL mode (trackpad frames, momentum tails, smooth-scroll mice):
 *    frames accumulate; once the stream shows clear intent
 *    (WHEEL_STREAM_COMMIT_PX) it steps exactly ONE capsule and absorbs the
 *    rest of the gesture — the finger motion AND the momentum tail that
 *    follows release — until the gesture ends (WHEEL_STREAM_IDLE_MS of
 *    silence) or the direction reverses (which re-arms the stream so a
 *    down-then-up swipe can step back, never double down).
 */
export function wheelGestureStep(
  gesture: WheelGesture,
  deltaY: number,
  deltaMode: number,
  at: number,
): {next: WheelGesture; step: 1 | -1 | 0} {
  if (deltaY === 0) {
    // Horizontal-only frames never step (and don't idle the stream out).
    return {next: {...gesture, lastEventAt: at}, step: 0}
  }
  const dir: 1 | -1 = deltaY > 0 ? 1 : -1
  if (deltaMode === WHEEL_LINE_MODE) {
    return {
      next: {...gesture, lastEventAt: at, accum: 0, absorbed: false, dir},
      step: dir,
    }
  }
  const fresh = at - gesture.lastEventAt > WHEEL_STREAM_IDLE_MS
  let accum: number
  let absorbed: boolean
  if (fresh) {
    accum = deltaY
    absorbed = false
  } else if (gesture.dir !== 0 && gesture.dir !== dir) {
    // Direction reversed mid-gesture: the old intent is dead; re-arm so the
    // reverse motion can step back (a round trip, never a double step).
    accum = deltaY
    absorbed = false
  } else {
    accum = gesture.accum + deltaY
    absorbed = gesture.absorbed
  }
  if (!absorbed && Math.abs(accum) >= WHEEL_STREAM_COMMIT_PX) {
    return {
      next: {lastEventAt: at, accum: 0, absorbed: true, dir},
      step: dir,
    }
  }
  return {next: {lastEventAt: at, accum, absorbed, dir}, step: 0}
}

/**
 * Elements a press must never turn into a pager drag and that keep their
 * own keyboard behavior while the pager is locked (buttons, links, inputs,
 * selects, role=button). Shared by the web pager's drag arming and the
 * controller's locked-keyboard branch so the two never drift.
 */
export const INTERACTIVE_SELECTOR =
  'button, a, input, textarea, select, [role="button"]'

/**
 * Pager input lock: play mode, the quiz, and the exit prompt all freeze the
 * feed — no wheel, drag, keys, or arrows may move it. Pure so the shell,
 * the controller, and the regression test share one definition.
 */
export function isPagerLocked(
  isPlayingMode: boolean,
  isShowingQuiz: boolean,
  isPromptOpen: boolean,
): boolean {
  return isPlayingMode || isShowingQuiz || isPromptOpen
}

/**
 * Fast-fling threshold: a release velocity at or above this (px/s, in the
 * scroll direction) counts as navigation intent even below THRESHOLD — the
 * TikTok-style rule "a strong flick commits a page".
 */
export const VELOCITY_COMMIT_PX_S = 900

/**
 * Minimum displacement (share of a page step) a fling must have for the
 * velocity alone to commit it — a tiny nudge must never navigate.
 */
export const VELOCITY_MIN_RATIO = 0.2

/** Full scroll step: one page plus its trailing gap. */
export function pageStep(pageHeight: number): number {
  return pageHeight + PAGE_GAP
}

/** The page a given scroll offset rests on (0-based, clamped to total). */
export function pageIndexAt(
  scrollTop: number,
  pageHeight: number,
  total: number,
): number {
  const step = pageStep(pageHeight)
  if (pageHeight <= 0 || step <= 0 || total <= 0) return 0
  return Math.max(0, Math.min(total - 1, Math.round(scrollTop / step)))
}

/** Direction to commit after a release: +1 next, -1 previous, 0 snap back. */
export type CommitDirection = 1 | -1 | 0

/**
 * Pure settle decision for one page step, used by the web pager on drag
 * release (the native pager mirrors the same rule via THRESHOLD + its
 * momentum clamp).
 *
 * Sign convention: `displacementRatio` and `releaseVelocity` are both
 * positive when the gesture moves toward the NEXT page (scrollTop
 * increasing), negative toward the previous page.
 *
 * Commits when the displacement already crossed THRESHOLD, or when the
 * release is a genuine fast fling (|velocity| >= VELOCITY_COMMIT_PX_S) that
 * also moved a meaningful distance. Returns the direction to commit, or 0
 * to snap back to the current page.
 */
export function shouldCommit(
  displacementRatio: number,
  releaseVelocity: number,
): CommitDirection {
  const r = Number.isFinite(displacementRatio) ? displacementRatio : 0
  const v = Number.isFinite(releaseVelocity) ? releaseVelocity : 0
  if (r >= THRESHOLD) return 1
  if (r <= -THRESHOLD) return -1
  if (v >= VELOCITY_COMMIT_PX_S && r >= VELOCITY_MIN_RATIO) return 1
  if (v <= -VELOCITY_COMMIT_PX_S && r <= -VELOCITY_MIN_RATIO) return -1
  return 0
}